import { ResourceLinkCard } from "@/components/cards/resource-link-card";
import { client } from "@/hono";
import { Combobox, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { ResourceLinkFormSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import z from "zod";

/** Props Interface */
interface ResourceLinkFormProps {
	projectId?: string;
	hideTicketPicker?: boolean;
}

export function ResourceLinkForm({ projectId, hideTicketPicker = false }: ResourceLinkFormProps) {
	/** Context */
	const methods = useFormContext<ResourceLinkFormSchema>();

	const [currentLink, currentName] = useWatch({ control: methods.control, name: ["link", "name"] });
	const isValidUrl = z.url({ hostname: z.regexes.domain }).safeParse(currentLink).success;

	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { data: tickets } = useQuery({
		queryKey: ["tickets", projectId ?? null],
		queryFn: async () => {
			if (!projectId) return [];
			const response = await client.projects[":projectId"].tickets.$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.tickets;
		},
		enabled: !hideTicketPicker && !!projectId,
	});

	/** Render */
	return (
		<React.Fragment>
			<TextInput
				autoFocus
				control={methods.control}
				name="link"
				label={t("resource_links.link")}
				placeholder={t("placeholders.web")}
				isRequired
				data-cy="resource-link-input"
			/>
			<TextInput
				control={methods.control}
				name="name"
				label={t("resource_links.name")}
				placeholder={t("placeholders.resource_link_name")}
				isRequired={false}
				data-cy="resource-link-name-input"
			/>
			{isValidUrl && (
				<ResourceLinkCard
					url={currentLink}
					name={currentName}
				/>
			)}
			{!hideTicketPicker && (
				<Combobox
					control={methods.control}
					name="ticketId"
					options={tickets ?? undefined}
					displayValue={(item) => `${item._type.name} / ${item.description}`}
					keyExtractor={(item) => item.id}
					label={t("projects.ticket")}
					placeholder={t("placeholders.search")}
					isRequired={false}
					data-cy="resource-link-ticket-input"
				/>
			)}
		</React.Fragment>
	);
}
